import { Capacitor } from '@capacitor/core';
import { IonContent, IonGrid, IonPage, IonRow, IonText } from '@ionic/react';
import * as Sentry from '@sentry/capacitor';
import { useEffect, useState } from 'react';

import styles from '@/pages/Dashboard.module.css';

import { CategoryHeader } from '@/components/CategoryHeader/CategoryHeader';
import { CustomLoading } from '@/components/Loading';
import { NoInternet } from '@/components/NoInternet/NoInternet';
import { QuickLinkGrid } from '@/components/QuickLinkGrid';
import { ReportCardGrid } from '@/components/ReportCardGrid';
import { SectionHeader } from '@/components/SectionHeader';
import { toast } from '@/components/Toaster/Toast';

import { mergeClassNames } from '@/utils';

import { useIsOnline } from '@/hooks/useIsOnline';

import { useReportContext } from '@/contexts/reportContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import { QSenseHubControl } from '@/plugins/QSenseHubControl';

export const Dashboard = () => {
  const { userInfo } = useUserInfoContext();
  const { reportInfoDict, isLoading, isError } = useReportContext();
  const isOnline = useIsOnline();

  const [bluetoothState, setBluetoothState] = useState<{
    usable: boolean;
    state: string;
    authorization: string;
    message: string;
    canRecover: boolean;
    action: string;
  } | null>(null);

  useEffect(() => {
    if (isError) {
      console.log('Error:', isError);
    }
  }, [isError]);

  const hideBluetoothNotification = !Capacitor.isNativePlatform() || !bluetoothState;

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const checkBluetoothState = async () => {
        try {
          const state = await QSenseHubControl.getBluetoothStateInfo();
          console.log('Bluetooth State:', state);
          setBluetoothState(state);
        } catch (error) {
          toast({
            variant: 'error',
            title: 'Bluetooth Error',
            description: 'Failed to get Bluetooth state information.',
            duration: 5000,
          });
          Sentry.captureException(error);
        }
      };

      checkBluetoothState();
    }
  }, []);

  const handleBluetoothAction = async () => {
    if (!bluetoothState) return;

    if (bluetoothState.action === 'openSettings') {
      try {
        await QSenseHubControl.openAppSettings();
      } catch (error) {
        toast({
          variant: 'error',
          title: 'Error',
          description: 'Failed to open settings.',
          duration: 5000,
        });
        Sentry.captureException(error);
      }
    }
  };

  return (
    <IonPage id='main'>
      <IonContent className={styles.container} color='light' id='main'>
        <IonGrid fixed style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SectionHeader title={`Welcome back, ${userInfo?.firstName}`} />
          <div className={styles.content}>
            <div className={styles.top}>
              <QuickLinkGrid />

              {!isOnline ? (
                <div className='ion-padding-top'>
                  <NoInternet
                    description='We could not load your data, make sure you have a stable internet connection'
                    title='You are currently offline'
                  />
                </div>
              ) : isLoading ? (
                <>
                  <div className={styles.loadingContainer}>
                    <CustomLoading
                      iconClassName={mergeClassNames(styles.icon, styles.spin)}
                      leadingIcon='spinner'
                      message='Loading reports, please wait...'
                      size='medium'
                    />
                  </div>
                </>
              ) : (
                <ReportCardGrid data={reportInfoDict} />
              )}
            </div>
            {!hideBluetoothNotification && !bluetoothState?.usable ? (
              <CategoryHeader
                buttonAction={bluetoothState?.action === 'openSettings' ? handleBluetoothAction : undefined}
                buttonText={bluetoothState?.action === 'openSettings' ? 'Open Settings' : undefined}
                message={bluetoothState?.message || 'Checking Bluetooth status...'}
                title='Bluetooth Info'
              />
            ) : null}
          </div>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};
